import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Fetch leads with optional filters.
 *   useLeads({ status: 'new_lead', limit: 50 })
 */
export function useLeads({ status, limit = 50, offset = 0 } = {}) {
  return useQuery({
    queryKey: ['leads', { status, limit, offset }],
    queryFn: async () => {
      const params = { limit, offset };
      if (status) params.status = status;
      const { data } = await api.get('/api/leads', { params });
      // The API returns { leads, total } - normalize:
      const leads = Array.isArray(data) ? data : data.leads || [];
      return { leads, total: Number(data.total ?? leads.length) };
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData, // smooth page-to-page transitions (no blank flash); v4's keepPreviousData: true is ignored by v5
  });
}

/**
 * Every lead in the workspace, for the Contacts page (owner, 29 Oct 2026: "i
 * can export just 1000 when i have over 2500+ leads"). The list route hands out
 * at most 1,000 a request, so this asks page after page, three at a time, up to
 * `cap`. The key starts with 'leads', so every lead change refreshes it.
 */
export function useAllLeads({ cap = 30000 } = {}) {
  return useQuery({
    queryKey: ['leads', 'all', cap],
    queryFn: async () => {
      const PAGE = 1000;
      const first = (await api.get('/api/leads', { params: { limit: PAGE, offset: 0 } })).data;
      const leads = [...(first.leads || [])];
      const total = Number(first.total ?? leads.length);
      const offsets = [];
      for (let o = PAGE; o < Math.min(total, cap); o += PAGE) offsets.push(o);
      for (let i = 0; i < offsets.length; i += 3) {
        const pages = await Promise.all(offsets.slice(i, i + 3).map((offset) =>
          api.get('/api/leads', { params: { limit: PAGE, offset } }).then((r) => r.data.leads || [])));
        for (const p of pages) leads.push(...p);
      }
      // A lead that arrives between two pages shifts the next page by one:
      // it must not be listed twice.
      const seen = new Set();
      return { leads: leads.filter((l) => !seen.has(l.id) && seen.add(l.id)), total };
    },
    staleTime: 30_000,
  });
}

/**
 * Archived leads (no activity for 30+ days) for a pipeline, for the Archive page.
 * Returns the raw array; the page groups them into per-stage tabs.
 */
export function useArchivedLeads(pipelineId) {
  return useQuery({
    queryKey: ['leads', 'archived', pipelineId],
    queryFn: async () => {
      const params = { scope: 'archived', limit: 1000 };
      if (pipelineId) params.pipeline_id = pipelineId;
      const { data } = await api.get('/api/leads', { params });
      return Array.isArray(data) ? data : data.leads || [];
    },
    enabled: !!pipelineId,
    staleTime: 30_000,
  });
}

/**
 * Restore an archived lead to the active board (bumps its activity stamp).
 */
export function useRestoreLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/leads/${id}/restore`, {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/**
 * Fetch a single lead by id.
 */
export function useLead(id) {
  // Normalise the id to a Number so the query key matches the numeric ids used
  // by the note/update mutations' invalidations. (The contact page passes a
  // STRING id from the URL; without this, saves didn't refetch and new notes
  // appeared to vanish.)
  const key = id != null && id !== '' ? Number(id) : null;
  return useQuery({
    queryKey: ['lead', key],
    enabled: Boolean(key) && !Number.isNaN(key),
    queryFn: async () => {
      const { data } = await api.get(`/api/leads/${id}`);
      return data;
    },
  });
}

/** Chronological activity timeline for a lead (meetings, emails, notes, WhatsApp, payments...). */
export function useLeadActivity(id) {
  return useQuery({
    queryKey: ['lead', id, 'activity'],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data } = await api.get(`/api/leads/${id}/activity`);
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30_000,
  });
}

/**
 * Add a free-form note onto a lead. Invalidates the single-lead query so
 * the drawer refetches with the new note appended.
 */
export function useAddLeadNote(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (note_text) =>
      (await api.post(`/api/leads/${leadId}/notes`, { note_text })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', Number(leadId)] });
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useDeleteLeadNote(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId) =>
      (await api.delete(`/api/leads/${leadId}/notes/${noteId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead', Number(leadId)] }),
  });
}

export function useUpdateLeadNote(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, note_text }) =>
      (await api.patch(`/api/leads/${leadId}/notes/${noteId}`, { note_text })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead', Number(leadId)] }),
  });
}

/**
 * Edit any of the lead's basic fields. The PUT endpoint whitelists allowed
 * columns, so callers can pass whatever subset they want.
 */
export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }) =>
      (await api.put(`/api/leads/${id}`, patch)).data,
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: ['lead', lead.id] });
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

// Replace a lead's tags (the editor sends the full new list).
export function useUpdateLeadTags(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tags) => (await api.patch(`/api/leads/${leadId}/tags`, { tags })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

// Pause / resume AUTOMATED messages (WhatsApp + email) for a lead.
export function useUpdateLeadComms(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paused) => (await api.patch(`/api/leads/${leadId}/comms`, { paused })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-threads'] });
    },
  });
}

/**
 * Hard-delete a lead and every cascade-linked record (notes, meetings,
 * email logs). The linked student row survives with lead_id = NULL so
 * revenue history is not destroyed.
 */
export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/api/leads/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
    },
  });
}

/**
 * Bulk hard-delete leads (Contacts multi-select). RLS scopes to the org.
 */
export function useBulkDeleteLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids) => (await api.post('/api/leads/bulk-delete', { ids })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
    },
  });
}

/**
 * Create a new lead. Returns the inserted row.
 * Invalidates all `leads` queries so the kanban refetches.
 */
export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/leads', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/**
 * Patch a lead's status (and optionally its followup_at when moving to follow-up).
 * Optimistically updates all relevant queries and arrays.
 */
export function useUpdateLeadStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, followup_at, deal_value, outcome_reason }) => {
      const body = { status };
      if (followup_at !== undefined) body.followup_at = followup_at;
      // In the workspace's currency (the column is deal_value_inr for history).
      if (deal_value !== undefined) body.deal_value = deal_value;
      if (outcome_reason !== undefined) body.outcome_reason = outcome_reason;
      const { data } = await api.patch(`/api/leads/${id}/status`, body);
      return data;
    },
    onMutate: async ({ id, status, followup_at }) => {
      await qc.cancelQueries({ queryKey: ['leads'] });
      const previous = qc.getQueriesData({ queryKey: ['leads'] });

      const apply = (lead) => {
        if (lead.id !== id) return lead;
        const next = { ...lead, status };
        // HONOUR WHAT THE CALLER SENT, AND GUESS AT NOTHING.
        //
        // This used to keep the date only for the stage literally keyed
        // follow_up and null it for every other one - so moving a lead into a
        // stage that ASKS for a date showed that date being wiped the instant
        // it was set, until the refetch put it back. The optimistic layer has
        // no idea which stages ask; the server does. So: use the value when one
        // was sent, clear it only on the legacy key that always cleared it, and
        // otherwise leave the field alone for the refetch to settle.
        if (followup_at !== undefined) next.followup_at = followup_at;
        else if (lead.status === 'follow_up' && status !== 'follow_up') next.followup_at = null;
        return next;
      };

      qc.setQueriesData({ queryKey: ['leads'] }, (old) => {
        if (!old) return old;
        if (Array.isArray(old)) return old.map(apply);
        if (old.leads) return { ...old, leads: old.leads.map(apply) };
        return old;
      });
      qc.setQueryData(['lead', id], (old) => (old ? apply(old) : old));

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        context.previous.forEach(([key, data]) => qc.setQueryData(key, data));
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Patch only the followup_at timestamp. Convenience hook for inline date pickers.
 * Status is implicitly switched to 'follow_up' server-side when a date is set.
 */
export function useUpdateLeadFollowup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, followup_at }) => {
      const { data } = await api.patch(`/api/leads/${id}/followup`, {
        followup_at,
      });
      return data;
    },
    onMutate: async ({ id, followup_at }) => {
      await qc.cancelQueries({ queryKey: ['leads'] });
      const previous = qc.getQueriesData({ queryKey: ['leads'] });

      const apply = (lead) => {
        if (lead.id !== id) return lead;
        return {
          ...lead,
          followup_at,
          status: followup_at ? 'follow_up' : lead.status,
        };
      };

      qc.setQueriesData({ queryKey: ['leads'] }, (old) => {
        if (!old) return old;
        if (Array.isArray(old)) return old.map(apply);
        if (old.leads) return { ...old, leads: old.leads.map(apply) };
        return old;
      });
      qc.setQueryData(['lead', id], (old) => (old ? apply(old) : old));

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        context.previous.forEach(([key, data]) => qc.setQueryData(key, data));
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Fetch follow-up leads grouped by urgency bucket.
 *   useFollowups('overdue' | 'today' | 'tomorrow' | 'week')
 */
export function useFollowups(bucket, includeMeetings = false) {
  return useQuery({
    queryKey: ['leads', 'follow_up', bucket, includeMeetings],
    queryFn: async () => {
      const { data } = await api.get('/api/leads/status/follow-up', {
        params: { bucket, ...(includeMeetings ? { include_meetings: 'true' } : {}) },
      });
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30_000,
  });
}

/**
 * A small composite: counts of leads by status (using lightweight calls).
 */
export function useLeadCounts() {
  const newQ = useLeads({ status: 'new_lead', limit: 1 });
  const followQ = useLeads({ status: 'follow_up', limit: 1 });
  const convertedQ = useLeads({ status: 'conversion', limit: 1 });

  return {
    new: newQ.data?.total ?? 0,
    followUp: followQ.data?.total ?? 0,
    converted: convertedQ.data?.total ?? 0,
    isLoading: newQ.isLoading || followQ.isLoading || convertedQ.isLoading,
  };
}
